<template>
  <div class="min-h-screen bg-gray-50" style="padding: 0px;margin: 0px;">
    <!-- 顶部搜索区域 -->
    <div class="z-50">
      <div class="mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="relative flex-1">
            <input
              type="text"
              class="w-full pl-10 pr-4 py-2 text-sm border-none bg-gray-100 !rounded-button"
              placeholder="搜索插件..."
              v-model="searchQuery"
            >
            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="mx-auto px-4 pt-2">
      <div class="flex gap-6">
        <!-- 左侧分类导航 -->
        <div class="w-60 bg-white rounded-lg shadow-sm p-4 h-fit sticky top-24">
          <h3 class="text-lg font-medium mb-4">插件分类</h3>
          <div class="space-y-2">
            <button
              v-for="category in filteredCategories"
              :key="category.plus_category_id"
              @click="selectC(category)"
              :class="{
                'bg-blue-50 text-blue-600': currentCategory === category.plus_category_id,
                'hover:bg-gray-50': currentCategory !== category.plus_category_id
              }"
              class="w-full px-4 py-2 text-left !rounded-button whitespace-nowrap flex items-center relative"
            >
              <i :class="category.icon" class="mr-3"></i>
              <span class="flex-1">{{ category.name }}</span>

              <!-- 改用计算后的未读状态 -->
              <!-- <span v-if="categoryUnreadMap[category.plus_category_id]?categoryUnreadMap[category.plus_category_id]:hasUnread" class="unread-dot"></span> -->

              <!-- 显示过滤后的插件数量（而非原始count） -->
              <span class="ml-2 text-sm text-gray-400">
                {{ baseFilteredPlugins.filter(p => p.plus_category_id === category.plus_category_id).length }}
              </span>
            </button>
          </div>
        </div>

        <!-- 右侧插件展示区域 -->
        <div class="flex-1">
          <div class="mb-6 flex items-center justify-between">
            <h2 class="text-xl font-medium">
              <span class="text-gray-400 text-sm ml-2">共 {{ filteredPlugins.length }} 个插件</span>
              <!-- 当前分类未读统计 -->
              <span v-if="currentCategoryUnreadCount > 0" class="ml-4 text-sm text-red-500 flex items-center">
                <span class="unread-dot-sm mr-1"></span>
                {{ currentCategoryUnreadCount }} 个未读
              </span>
            </h2>
          </div>
          <div class="grid grid-cols-4 gap-6">
            <div
              v-for="(plugin, index) in filteredPlugins"
              :key="plugin.id"
              class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 flex flex-col relative plugin-card"
            >
              <!-- 插件未读标记 - 右上角红点 -->
              <div v-if="plugin.is_read === 0" class="absolute top-3 right-3">
                <span class="unread-dot-lg"></span>
              </div>

              <!-- 修改：添加点击事件 -->
              <div @click="handlePluginClick(plugin)" class="flex flex-col flex-1 cursor-pointer">
                <div class="w-16 h-16 rounded-lg overflow-hidden mb-4 mx-auto relative">
                  <img v-if="plugin.logo" :src="plugin.logo" class="w-full h-full object-cover" style="border: none;">
                  <Icon
                    v-else
                    :iconname="hasIcon(plugin.ico)"
                    class="w-full h-full object-cover text-blue-500"
                  />
                  <!-- 图标右上角红点 -->
                  <span v-if="plugin.is_read === 0" class="absolute -top-1 -right-1 unread-dot-md"></span>
                </div>

                <h4 class="text-lg font-medium mb-2 text-center relative" style="font-size:15px;margin-top: 0px;">
                  <!-- {{ index === 0 ? getDisplayName(plugin.name) : plugin.name }} -->
                  {{ plugin.name  }}
                  <!-- 插件名称后的红点 -->
                  <span v-if="plugin.is_read === 0" class="absolute -top-1 -right-4 unread-dot-sm"></span>
                </h4>

                <p class="text-gray-600 text-sm mb-4 flex-1 text-center" style="font-size:12px;line-height: 16px;">
                  {{ plugin.remark || plugin.description }}
                </p>

                <div class="flex items-center justify-between mt-auto pt-4 border-t">
                  <span class="text-xs text-gray-400">v{{ plugin.version }}</span>
                  <!-- 底部状态指示器 -->
                  <div v-if="plugin.is_read === 0" class="flex items-center">
                    <span class="text-xs text-red-500 mr-1">未读</span>
                    <span class="unread-dot-xs"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PlusApi from '@/api/plus.js';
import CommonApi from '@/api/common.js';
import { getSessionStorage } from '@/utils/base'

export default {
  data() {
    return {
      shopusercode: '',
      shopuserid: '',
      loading: true,
      searchQuery: '',
      currentCategory: "",
      listData: [], // 从API获取的原始数据
      categories: [], // 处理后的分类数据
      plugins: [], // 处理后的插件数据
    };
  },
  computed: {
    // 先定义「基础过滤后的插件」（仅权限+搜索，不含分类）
    baseFilteredPlugins() {
      return this.plugins.filter(plugin =>
        this.hasPermission(plugin) &&
        (plugin.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (plugin.description && plugin.description.toLowerCase().includes(this.searchQuery.toLowerCase())))
      );
    },

    // 左侧分类过滤（基于基础过滤后的插件）
    filteredCategories() {
      return this.categories.filter(category =>
        // 只显示「基础过滤后插件」中存在的分类
        this.baseFilteredPlugins.some(plugin =>
          plugin.plus_category_id === category.plus_category_id
        )
      );
    },

    // 右侧插件过滤（基础过滤 + 分类匹配）
    filteredPlugins() {
      return this.baseFilteredPlugins.filter(plugin =>
        plugin.plus_category_id === this.currentCategory
      );
    },

    // 重新计算分类的未读状态（基于基础过滤后的插件）
    // categoryUnreadMap() {
    //   const map = {};
    //   this.categories.forEach(category => {
    //     // 只计算「基础过滤后」的插件未读数量
    //     const categoryPlugins = this.baseFilteredPlugins.filter(
    //       plugin => plugin.plus_category_id === category.plus_category_id
    //     );
    //     map[category.plus_category_id] = {
    //       hasUnread: categoryPlugins.some(plugin => plugin.is_read === 0),
    //       unreadCount: categoryPlugins.filter(plugin => plugin.is_read === 0).length
    //     };
    //   });
    //   return map;
    // },

    // 当前分类未读数量（基于最终过滤后的插件）
    currentCategoryUnreadCount() {
      return this.filteredPlugins.filter(plugin => plugin.is_read === 0).length;
    }
  },
  mounted() {
    this.initData(); // 改为统一的初始化方法
  },
  methods: {
    // 新增：处理插件点击事件
    async handlePluginClick(plugin) {
      try {
        // 如果插件是未读状态，更新为已读
        if (plugin.is_read === 0) {
          // 调用API更新阅读状态
          // await PlusApi.markAsRead({
          //   access_id: plugin.access_id
          // });

          // 前端更新状态
          plugin.is_read = 1;

          // 更新分类的未读状态
          this.updateCategoryUnreadStatus();

          console.log(`插件 ${plugin.name} 标记为已读`);
        }

        // 跳转到插件页面
        if (plugin.path) {
          this.$router.push(plugin.path);
        }
      } catch (error) {
        console.error('标记已读失败:', error);
        // 即使标记失败也继续跳转
        if (plugin.path) {
          this.$router.push(plugin.path);
        }
      }
    },

    // 新增：更新分类未读状态
    updateCategoryUnreadStatus() {
      this.categories.forEach(category => {
        const categoryPlugins = this.plugins.filter(
          plugin => plugin.plus_category_id === category.plus_category_id
        );
        category.hasUnread = categoryPlugins.some(plugin => plugin.is_read === 0);
        category.unreadCount = categoryPlugins.filter(plugin => plugin.is_read === 0).length;
      });
    },

    getDisplayName() {
      switch(this.shopusercode) {
        case 'admin': return '企业';
        case 'company': return '企业';
        case 'government': return '政府';
        case 'shanghui': return '商会';
        case 'kuajing': return '跨境云';
        default: return '默认名称';
      }
    },
    async initData() {
      try {
        this.loading = true;
        // 先获取角色/权限数据
        await this.getRole();
        // 再获取插件列表
        await this.getData();
      } catch (error) {
        console.error('初始化数据失败:', error);
      } finally {
        this.loading = false;
      }
    },

    async getRole() {
      try {
        const res = await CommonApi.getRole({}, true);
        this.shopuserid = res.msg.shopUserId;
        this.shopusercode = res.msg.code;
        console.log('用户角色:', this.shopusercode);
      } catch (error) {
        console.error('获取角色失败:', error);
        throw error; // 抛出错误，让initData捕获
      }
    },

    async getData() {
      try {
        const res = await PlusApi.plusList({}, true);
        this.listData = res.data.list;
        console.log('原始数据:', this.listData);
        this.processData();
      } catch (error) {
        console.error('获取插件列表失败:', error);
        throw error; // 抛出错误，让initData捕获
      }
    },

    selectC(item) {
      this.currentCategory = item.plus_category_id;
    },

    async getRole() {
      try {
        const res = await CommonApi.getRole({}, true);
        this.shopuserid = res.msg.shopUserId;
        this.shopusercode = res.msg.code;
        console.log('用户角色:', this.shopusercode); // 调试日志
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    // 处理原始数据
    processData() {
      this.categories = [];
      this.plugins = [];

      this.listData.forEach(category => {
        // 简化分类数据（只保留基础信息，未读状态由computed计算）
        const newCategory = {
          plus_category_id: category.plus_category_id,
          name: category.name,
          icon: category.icon || 'fas fa-cube',
          count: category.children.length // 原始count，模板中会替换为过滤后的数量
        };
        this.categories.push(newCategory);

        // 处理插件数据
        category.children.forEach(plugin => {
          const isRead = plugin.is_read !== undefined ? plugin.is_read : 1;

          this.plugins.push({
            id: plugin.access_id,
            access_id: plugin.access_id,
            plus_category_id: category.plus_category_id,
            name: plugin.name,
            description: plugin.remark,
            version: plugin.version,
            path: plugin.path,
            ico: plugin.ico,
            logo: plugin.logo || this.generateDefaultLogo(plugin.name),
            is_read: isRead
          });
        });
      });

      // 设置默认选中第一个分类
      if (this.categories.length > 0) {
        this.currentCategory = this.categories[0].plus_category_id;
      }
    },
    // 生成默认logo
    generateDefaultLogo(name) {
      return `/static/imgs/plus.jpg`;
    },

    // 权限验证 - 临时返回true以测试显示
    hasPermission(plugin) {
      // 临时返回true，确保所有插件都能显示
      // return true;

      // 如果需要恢复权限验证，取消注释下面的代码

      const authList = getSessionStorage('authlist') || {};
      // console.log('authList',authList);

      const hasAuth = authList[plugin.path.toLowerCase()] === true;

      // 调试权限验证
      if (plugin.name === '商会') {
         console.log('authList',authList)
         console.log('plugin.path.toLowerCase()',authList[plugin.path.toLowerCase()])
        console.log(`商会插件权限检查:`, {
          path: plugin.path,
          authList: authList,
          hasAuth: hasAuth
        });
      }
      // console.log(hasAuth,'hasAuth')
      // console.log( plugin.path,' plugin.path')
      // console.log( '---------------------')
      return hasAuth;
    },

    hasCategoryPermission(category) {
      // 临时返回true，确保所有分类都能显示
      return true;
    },

    // 图标处理
    hasIcon(ico) {
      return ico && ico.length > 0 ? ico : '#icon-shop-applications';
    }
  },
  created() {
    this.getData();
  }
};
</script>

<style scoped>
/* 保留原有样式不变 */
.plugin-item {
  transition: all 0.2s ease;
}
.plugin-item:hover {
  transform: translateY(-2px);
}

.plus-container{ min-height: 400px;}
.plus-container .common-form{ margin-bottom: 0;}
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
textarea {
  resize: none;
}

/* 未读标记样式 - 红点 */
.unread-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #ff4757;
  border-radius: 50%;
  margin-left: 8px;
  animation: pulse 2s infinite;
}

.unread-dot-lg {
  display: inline-block;
  width: 12px;
  height: 12px;
  background-color: #ff4757;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
  box-shadow: 0 0 0 2px rgba(255, 71, 87, 0.3);
}

.unread-dot-md {
  display: inline-block;
  width: 10px;
  height: 10px;
  background-color: #ff4757;
  border-radius: 50%;
  animation: pulse 1.8s infinite;
  box-shadow: 0 0 0 1px rgba(255, 71, 87, 0.3);
}

.unread-dot-sm {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #ff4757;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.unread-dot-xs {
  display: inline-block;
  width: 4px;
  height: 4px;
  background-color: #ff4757;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.plugin-card {
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.plugin-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* 未读卡片特殊样式 */
.plugin-card:has(.unread-dot-lg) {
  border: 1px solid #ffdad6;
  background: linear-gradient(135deg, #fff, #fff5f5);
}

.plugin-card:has(.unread-dot-lg):hover {
  border-color: #ff4757;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.15);
}
</style>



// WEBPACK FOOTER //
// src/views/plus/plus/index.vue